import React from 'react'
import {
    Container, Row,
    Col, Label, Input, Button,
    Table,
} from 'reactstrap'
import DropDownTextField from '../view/DropDownTextField'
import { ContactInformationViewController } from './ContactInformationViewController'
import { AddressViewController } from './AddressViewController'
import {updateAccountSettings} from '../model/request/login'
import {Settings, Roles, Account} from '../model/Account'
import {
    IoIosArrowUp,
    IoIosArrowDown,
} from 'react-icons/io';
import TimePicker from 'react-time-picker';

// LiquidGlass.css's heavy backdrop-filter usage has real perf/positioning
// bugs on every non-Firefox engine we've tested (Chromium and WebKit/Safari
// both show it, just Firefox doesn't) - rather than detecting this at every
// app load, decide once here when the theme is actually selected and
// persist the extra override file (MakeItWorkOnChrome.css) alongside it in
// theme_settings, same as any other selectable theme file.
function isFirefox() {
    return navigator.userAgent.includes('Firefox')
}

class SettingsViewController extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            'settings': Settings.fromLocalStorage(),
            'showAdvance': false,
            // null = use the real clock. An hour 0-23 previews what the
            // theme schedule would show at that time of day.
            'preview_hour': null,
        }
        this.appliedThemeKey = this.themeKey()
    }

    /**
     * Fingerprint of everything the preview depends on.
     *
     * Compared against our own stored copy rather than componentDidUpdate's
     * prevState: every theme control mutates this.state.settings in place
     * and hands the same object back to setState, so prevState.settings IS
     * this.state.settings and comparing the two always finds them equal.
     * @return {string}
     */
    themeKey() {
        return JSON.stringify(this.state.settings.theme_settings) +
            '|' + this.state.preview_hour
    }

    // Every theme edit funnels through setState({settings}), so one check
    // here covers every control - the time pickers, the theme dropdown,
    // add/remove and the reorder arrows - without each of them having to
    // remember to trigger a preview.
    componentDidUpdate() {
        const key = this.themeKey()
        if (key !== this.appliedThemeKey) {
            this.appliedThemeKey = key
            this.applyThemes()
        }
    }

    // Preview is in-memory only (nothing is saved until the Save button
    // calls updateSettings), so restoring from localStorage puts back
    // whatever was actually saved.
    componentWillUnmount() {
        this.applyThemes(Settings.fromLocalStorage(), new Date())
    }

    /**
     * The clock the preview pretends it is.
     * @return {Date}
     */
    previewDate() {
        const date = new Date()
        if (this.state.preview_hour !== null) {
            date.setHours(this.state.preview_hour, 0, 0, 0)
        }
        return date
    }

    /**
     * Swaps the live stylesheets for the ones `settings` would produce at
     * `now`. File themes are found by their /themes/ href - App.js inserts
     * them and nothing else in <head> matches that - and custom themes are
     * inline <style> tags matched by body, or by our own marker once this
     * has run at least once.
     * @param {Settings} settings
     * @param {Date} now
     */
    applyThemes(settings=this.state.settings, now=this.previewDate()) {
        const head = document.getElementsByTagName('head')[0]
        const customBodies = new Set(
            settings.theme_settings.themes.map((theme) => theme.body))

        head.querySelectorAll('link[href*="/themes/"]')
            .forEach((node) => node.remove())
        head.querySelectorAll('style').forEach((node) => {
            if (node.dataset.themePreview || customBodies.has(node.innerHTML)) {
                node.remove()
            }
        })

        // Appended in item order - later stylesheets win the cascade, which
        // is what the reorder arrows above are for.
        const themeNames = settings.theme_settings.getThemeNames(now)
        for (let i = 0; i < themeNames.length; i++) {
            let node = null
            if (themeNames[i].is_custom) {
                node = document.createElement('style')
                node.innerHTML = themeNames[i].value
                node.dataset.themePreview = '1'
            } else {
                node = document.createElement('link')
                node.setAttribute('rel', 'stylesheet')
                node.setAttribute('type', 'text/css')
                node.setAttribute('href', themeNames[i].value)
            }
            head.appendChild(node)
        }
    }

    updateSettings() {
        this.props.turnOnLoader()
        updateAccountSettings(this.state.settings)
        .then(
            (result) => {
                this.state.settings.saveToLocalStorage()
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }


    // MARK: Render

    /**
     * Scrubs the previewed hour so a time-scheduled theme can be seen
     * without waiting for that time of day. Preview only - saving is
     * unaffected.
     */
    renderPreviewHour() {
        const hour = this.state.preview_hour
        const label = (h) => ('' + h).padStart(2, '0') + ':00'
        return <Row style={{'margin': '8px'}}>
            <Col>
                <Label style={{'fontSize': '20px'}}>
                    {'Preview Themes At: ' +
                        (hour === null ? 'Now' : label(hour))}
                </Label>
                <div style={{
                    'display': 'flex',
                    'alignItems': 'center',
                    'gap': '8px',
                    'maxWidth': '400px',
                }}>
                    <Label>{label(0)}</Label>
                    <Input
                        type="range"
                        min={0}
                        max={23}
                        value={hour === null ? new Date().getHours() : hour}
                        onChange={(e) => {
                            this.setState({
                                'preview_hour': Number.parseInt(e.target.value),
                            })
                        }} />
                    <Label>{label(23)}</Label>
                </div>
                <Button
                    disabled={hour === null}
                    onClick={(e) => {
                        this.setState({'preview_hour': null})
                    }}>Back To Now</Button>
            </Col>
        </Row>
    }

    renderCheck(label, varName) {
        return <>
            <Row>
                <Col><Label style={{
                    'fontSize': '20px',
                    'fontWeight': '500',
                }}>{label}</Label></Col>
                <Col>
                    <Input
                        type="checkbox"
                        checked={this.state.settings[varName]}
                        onChange={(e) => {
                            const settings = this.state.settings
                            settings[varName] = !settings[varName]
                            this.setState({
                                'settings': settings,
                            })
                        }} />
                </Col>
            </Row>
        </>
    }

    renderTextField(label, varName) {
        return <>
            <Row>
                <Col><Label style={{
                    'fontSize': '20px',
                    'fontWeight': '500',
                }}>{label}</Label></Col>
                <Col>
                    <Input
                        value={this.state.settings[varName]}
                        type="text"
                        onChange={(e) => {
                            const settings = this.state.settings
                            settings[varName] = e.target.value
                            this.setState({
                                'settings': settings,
                            })
                        }} />
                </Col>
            </Row>
        </>
    }

    renderAsanteContactInfo() {
        function renderInput(label, varName, context) {
            return <Col>
                <Label>{label}</Label>
                <Input
                    value={context.state.settings.asante_contact_info[varName]}
                    onChange={(e) => {
                        const settings = context.state.settings
                        settings.asante_contact_info[varName] = e.target.value
                        context.setState({
                            'settings': settings,
                        })
                    }} />
            </Col>
        }

        return <Row>
            <Col>
                <Container style={{
                    'marginLeft': '23px',
                    'maxWidth': '655px',
                }}><Row><Col>
                        <Label style={{
                            'fontSize': '20pt', 'fontWeight': '400'
                        }}>Asante Sign Group Contact Information</Label>
                    </Col></Row>
                    <Row style={{'marginLeft': '0px'}}>
                        {renderInput('First Name', 'first_name', this)}
                        {renderInput('Last Name', 'last_name', this)}
                    </Row>
                    <Row style={{'marginLeft': '0px'}}>
                        {renderInput('Email', 'email', this)}
                    </Row>
                </Container>
            </Col>
        </Row>
    }

    renderThemes() {
        return <Row><Col><div style={{'overflowX': 'auto'}}><Table>
            <thead>
                <tr>
                    <th
                        className="text-center label-weight"
                        style={{'minWidth': '200px'}}>Name</th>
                    <th
                        className="text-center label-weight"
                        style={{'minWidth': '500px'}}>CSS File</th>
                </tr>
            </thead>
            <tbody>
            {this.state.settings.theme_settings.themes.map((item, index) => {
                return <tr>
                    <td>
                        <Input
                            value={item.name}
                            onChange={(e) => {
                                item.name = e.target.value
                                this.setState({
                                    'settings': this.state.settings,
                                })
                            }} />
                    </td>
                    <td>
                        <Input
                            type="textarea"
                            value={item.body}
                            onChange={(e) => {
                                item.body = e.target.value
                                this.setState({
                                    'settings': this.state.settings,
                                })
                            }}
                            style={{
                                'width': '100%',
                                'height': '200px'
                            }} />
                    </td>
                    <td>
                        <Button
                            color='danger'
                            onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.themes = [
                                    ...settings.theme_settings.themes.slice(0,index),
                                    ...settings.theme_settings.themes.slice(index+1),
                                ]
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Delete</Button>
                    </td>
                </tr>
            })}
            <tr>
                <td>
                    <Button
                        color='success'
                        onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.addTheme()
                            this.setState({
                                'settings': settings,
                            })
                        }}
                    >Add</Button>
                </td>
            </tr>
            </tbody>
            </Table>
            </div>
            </Col>
        </Row>
    }

    renderThemeTimes() {

        function flipThemeTimes(ctx, index, plusMinus) {
            const settings = ctx.state.settings

            if (index < 0 || index + plusMinus < 0 ||
                index >= settings.theme_settings.items.length ||
                index + plusMinus >= settings.theme_settings.items.length) {
                return
            }
            const rep = settings.theme_settings.items[index]
            settings.theme_settings.items[index] =
                settings.theme_settings.items[index+plusMinus]
            settings.theme_settings.items[index+plusMinus] = rep

            ctx.setState({
                'settings': settings,
            })
        }

        return <Row><Col><div style={{'overflowX': 'auto'}}><Table>
            <thead>
                <tr>
                    <th />
                    <th
                        className="text-center label-weight"
                        style={{'minWidth': '200px'}}>Start Time</th>
                    <th
                        className="text-center label-weight"
                        style={{'minWidth': '200px'}}>End Time</th>
                    <th
                        className="text-center label-weight"
                        style={{'minWidth': '400px'}}>Theme</th>
                </tr>
            </thead>
            <tbody>
            {this.state.settings.theme_settings.items.map((item, index) => {
                return <tr>
                    <td>
                        <Button
                            id={'move-sign-up-button-' + index}
                            style={{
                                'height': '10pt',
                                'margin': '0pt',
                                'padding': '0pt',
                                'marginTop': '-10pt',
                            }}
                            size='sm'
                            onClick={(e) => {
                                flipThemeTimes(this, index, -1)
                            }}>
                            <IoIosArrowUp 
                                style={{
                                    'margin-top': '-10pt',
                                    'margin': '0pt',
                                    'padding': '0pt',
                                    'fontSize': '10pt',
                                    'fontWeight': 'bold',
                                    'marginTop': '-10pt',
                                }}/>
                        </Button>
                        <Button
                            id={'move-sign-down-button-' + index}
                            style={{
                                'height': '10pt',
                                'margin': '0pt',
                                'padding': '0pt',
                                'marginTop': '-10pt',
                            }}
                            size='sm'
                            onClick={(e) => {
                                flipThemeTimes(this, index, 1)
                            }}>
                            <IoIosArrowDown
                                style={{
                                    'margin-top': '-10pt',
                                    'margin': '0pt',
                                    'padding': '0pt',
                                    'fontSize': '10pt',
                                    'fontWeight': 'bold',
                                    'marginTop': '-10pt',
                                }}/>
                        </Button>
                    </td>
                    <td>
                        <TimePicker
                            onChange={(value) => {
                                if (value) {
                                    const settings = this.state.settings
                                    settings.theme_settings.items[index].start_time = '' + value
                                    this.setState({
                                        'settings': settings,
                                    })
                                }
                            }}
                            value={item.start_time}
                        />
                    </td>
                    <td>
                        <TimePicker
                            onChange={(value) => {
                                if (value) {
                                    const settings = this.state.settings
                                    settings.theme_settings.items[index].end_time = '' + value
                                    this.setState({
                                        'settings': settings,
                                    })
                                }
                            }}
                            value={item.end_time}
                        />
                    </td>
                    <td>
                        <DropDownTextField
                            value={item.name}
                            values={[
                                ...this.state.settings.theme_settings.themes.map(
                                    (item) => item.name
                                ),
                                '/themes/GoogleColorPalette.css',
                                '/themes/DarkGruvBox.css',
                                '/themes/DarkMode.css',
                                '/themes/ClaudeDark.css',
                                '/themes/ClaudeLight.css',
                                '/themes/Apple.css',
                                '/themes/Stripe.css',
                                '/themes/Linear.css',
                                '/themes/Craft.css',
                                '/themes/LightGruvBox.css',
                                '/themes/HomeBrew.css',
                                '/themes/Wood.css',
                                '/themes/Chess.css',
                                '/themes/Denya.css',
                                '/themes/KingdomHearts.css',
                                '/themes/Halloween.css',
                                '/themes/CoffeeShop.css',
                                '/themes/Mario.css',
                                '/themes/Links/Dark/Blue.css',
                                '/themes/Links/Dark/Graphite.css',
                                '/themes/Links/Dark/Green.css',
                                '/themes/Links/Dark/Green2.css',
                                '/themes/Links/Dark/Orange.css',
                                '/themes/Links/Dark/Pink.css',
                                '/themes/Links/Dark/Purple.css',
                                '/themes/Links/Dark/Red.css',
                                '/themes/Links/Dark/Yellow.css',
                                '/themes/Links/Light/Blue.css',
                                '/themes/Links/Light/Graphite.css',
                                '/themes/Links/Light/Green.css',
                                '/themes/Links/Light/Orange.css',
                                '/themes/Links/Light/Pink.css',
                                '/themes/Links/Light/Purple.css',
                                '/themes/Links/Light/Red.css',
                                '/themes/Links/Light/Yellow.css',
                                '/themes/Links/Light/White.css',
                                '/themes/Links/Glow/Purple.css',
                                '/themes/Links/Glow/Cyan.css',
                                '/themes/BabyBlue.css',
                                '/themes/FireBrick.css',
                                '/themes/Sunset.css',
                                '/themes/PeakMusic.css',
                                '/themes/AsanteSignGroup.css',
                                '/themes/AsantePattern.css',
                                '/themes/text/LightOnDarkInput.css',
                                '/themes/text/LightFontTheme.css',
                                '/themes/Round.css',
                                '/themes/Squircle.css',
                                '/themes/MoneyColors.css',
                                '/themes/DateColors.css',
                                '/themes/Elevation.css',
                                '/themes/LightElevation1.css',
                                '/themes/LightElevation2.css',
                                '/themes/LightElevation3.css',
                                '/themes/MaterialDesign.css',
                                '/themes/QuoteSavePDFEmailRowStaysAtBottom.css',
                                '/themes/iOS6.css',
                                '/themes/iOS7.css',
                                '/themes/LiquidGlass.css',
                                '/themes/Aqua.css',
                                '/themes/Windows7.css',
                                '/themes/WindowsXP.css',
                                '/themes/Windows95.css',
                                '/themes/NeXTSTEP.css',
                                '/themes/Synthwave.css',
                                '/themes/Dracula.css',
                                '/themes/Nord.css',
                                '/themes/Catppuccin.css',
                                '/themes/TokyoNight.css',
                                '/themes/CompanySkins.css',
                                '/themes/StickyTableHeader.css',
                                '/themes/StickyFirstColumn.css',
                                '/themes/AlwaysShowScrollbars.css',
                                '/themes/DenseTable.css',
                                '/themes/WideMode.css',
                                '/themes/ZebraStripedRows.css',
                                '/themes/Fonts/EasyRead.css',
                                '/themes/Fonts/Elegant.css',
                                '/themes/Fonts/CleanSans.css',
                                '/themes/Fonts/ClassicSerif.css',
                                '/themes/Fonts/SFPro.css',
                                '/themes/Fonts/Sakura.css',
                                '/themes/Fonts/Rounded.css',
                                '/themes/Fonts/SignPainter.css',
                                '/themes/Fonts/TerminalMono.css',
                                '/themes/Fonts/Hack.css',
                                'https://wire.com/wp-content/themes/wire/assets/css/custom.css',
                                'https://www.apple.com/v/home/aj/built/styles/main.built.css',
                            ].filter((x) => x.toLowerCase().match(item.name.toLowerCase().split(' ').join('.*')))}
                            onChange={(e) => {
                                item.name = e.target.value
                                this.setState({
                                    'settings': this.state.settings,
                                })
                            }}
                            onClickRow={(value, index) => {
                                item.name = value
                                this.setState({
                                    'settings': this.state.settings,
                                })
                            }} />
                    </td>
                    <td>
                        <Button
                            color='danger'
                            onClick={(e) => {
                                const settings = this.state.settings
                                const items = settings.theme_settings.items
                                settings.theme_settings.items =
                                    [...items.slice(0, index), ...items.slice(index+1)]
                                this.setState({
                                    'settings': this.state.settings,
                                })
                            }}>Delete</Button>
                    </td>
                </tr>
            })}
            <tr>
                <td colspan="2">
                    <Button
                        color='success'
                        onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.addTimeItem()
                            this.setState({
                                'settings': settings,
                            })
                        }}
                    >Add</Button>
                </td>
            </tr>
            </tbody>
            </Table>
            </div>
            </Col>
        </Row>
    }

    render() {
        const account = Account.fromCookieAndLocalStorage()
        return (
            <Container>
                <Row>
                    <Col><Button
                        onClick={(e) => {
                            this.setState({
                                'showAdvance': !this.state.showAdvance,
                            })
                        }}
                        >{this.state.showAdvance ?
                            'Show Less' :
                            'Show Advanced Features'}</Button></Col>
                </Row>
                    {this.renderCheck('Excel Arrow Keys', 'excel_arrow_keys')}
                    {this.renderCheck('Auto Type Sign Type', 'auto_type_sign_type')}
                {this.state.showAdvance ? <>
                    {this.renderCheck('Show Digits Keypad', 'numbers_calculator_show')}
                    {this.renderCheck('Import Export Sign JSON', 'import_export_sign_json')}
                    {this.renderCheck('Quote Sign Editing Goes To Top', 'sign_editing_top')}
                    </> : <></>}
                {this.renderTextField('Quote Number Prefix', 'quote_number_prefix')}
                {account.hasRole(Roles.Asante) ?
                    this.renderAsanteContactInfo() : <></>}
                {account.hasRole(Roles.Asante) ? <></> :
                <Row>
                    <Col>
                        <Container style={{'maxWidth': '700px', 'marginLeft': '8px'}}>
                            <ContactInformationViewController
                                model={this.state.settings.contact_info}
                                onUpdate={(fieldName, value) => {
                                    const newSettings = this.state.settings
                                    newSettings.contact_info[fieldName] = value
                                    this.setState({
                                        'settings': newSettings,
                                    })
                                }} />
                        </Container>
                    </Col>
                </Row>
                }
                {account.hasRole(Roles.Asante) ? <></> :
                <Row>
                    <Col>
                        <Container style={{'maxWidth': '700px', 'marginLeft': '8px'}}>
                            <AddressViewController
                                model={this.state.settings.shipping_info}
                                onUpdate={(fieldName, value) => {
                                    const newSettings = this.state.settings
                                    newSettings.shipping_info[fieldName] = value
                                    this.setState({
                                        'settings': newSettings,
                                    })
                                }} />
                        </Container>
                    </Col>
                </Row>
                }
                {this.state.showAdvance ? <>
                    <Row style={{'marginTop': '10px'}}>
                        <Col>
                            <Label style={{
                                'fontSize': '20px',
                                'fontWeight': '500',
                            }}>Themes</Label>
                        </Col>
                    </Row>
                    <Row style={{'marginTop': '10px'}}>
                        <Col>
                            <Label style={{
                                'fontSize': '20px',
                                'fontWeight': '500',
                            }}>Custom Themes</Label>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Label>
                                Use CSS to create custom themes. Allows pro users
                                to make custom css to theme the website to their own preferance.
                                Here is examples <a href='/themes/DarkGruvBox.css'>dark gruv box</a>,
                                <a href='/themes/DarkMode.css'>dark mode</a>,
                                <a href='/themes/Links/Dark/Blue.css'> blue primary color</a>,
                                <a href='/themes/text/LightFontTheme.css'> light font theme</a>,
                                <a href='/themes/Round.css'>round theme</a>,
                                <a href='/themes/HomeBrew.css'>home brew theme</a>,
                                <a href='/themes/Wood.css'>wood theme</a>,
                                <a href='https://www.apple.com/v/home/aj/built/styles/main.built.css'>Apple's theme</a>.
                                Make sure to only keep the css you want as all will be applied.
                            </Label>
                        </Col>
                    </Row>
                    {this.renderThemes()}
                    <Row style={{'marginTop': '10px'}}>
                        <Col>
                            <Label style={{
                                'fontSize': '20px',
                                'fontWeight': '500',
                            }}>Time Picker</Label>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Label>
                                Pick the start and end time for any predefined theme or
                                    select a custom theme you made above.
                                A good example is to use a dark theme
                                    from 5:00pm - 11:59pm and from 12:00am - 7:00am.
                                A good dark theme is to use all 3,
                                    /themes/DarkGruvBox.css,
                                    /themes/text/LightOnDarkInput.css and /themes/Round.css.
                                To look at themes type in /themes for a theme done by asante or https
                                    for themes done by other websites. You can use any css file
                                    found on any website or one of your themes above.
                            </Label>
                        </Col>
                    </Row>
                    {this.renderThemeTimes()}
                </> : <></>}
                <Row style={{'margin': '8px', 'marginTop': '20px'}}>
                    <Col><Label style={{
                        'fontWeight': 'bold',
                        'fontSize': '30px',
                    }}>Our most popular themes are below. Just click a theme, then save, then refresh.</Label></Col>
                </Row>
                <Row>
                    <Col>
                        <Label
                            style={{
                                'fontWeight': '600',
                                'fontSize': '14pt',
                            }}
                        >Dark Themes</Label>
                        <Button
                            style={{
                                'margin': '8px'
                            }}
                            color='primary' onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.items = []
                            settings.theme_settings.addTimeItem('/themes/DarkMode.css')
                            settings.theme_settings.addTimeItem('/themes/Links/Dark/Green2.css')
                            this.setState({
                                'settings': settings,
                            })
                        }}>Standard Theme</Button>
                        <Button
                            style={{
                                'margin': '8px'
                            }}
                            color='primary' onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.items = []
                            settings.theme_settings.addTimeItem('/themes/DarkGruvBox.css')
                            settings.theme_settings.addTimeItem('/themes/Links/Dark/Yellow.css')
                            settings.theme_settings.addTimeItem('/themes/Round.css')
                            settings.theme_settings.addTimeItem('/themes/Elevation.css')
                            this.setState({
                                'settings': settings,
                            })
                        }}>Gruv Box Theme</Button>
                        <br/>
                        <Label
                            style={{
                                'fontWeight': '600',
                                'fontSize': '14pt',
                            }}
                            >Light Themes</Label>
                        <Button
                            style={{
                                'margin': '8px'
                            }}
                            color='primary' onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.items = []
                            this.setState({
                                'settings': settings,
                            })
                        }}>Standard Theme</Button>
                        <Button
                            style={{
                                'margin': '8px'
                            }}
                            color='primary' onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.items = []
                            settings.theme_settings.addTimeItem('/themes/GoogleColorPalette.css')
                            this.setState({
                                'settings': settings,
                            })
                        }}>Google Theme</Button>
                        <Button
                            style={{
                                'margin': '8px'
                            }}
                            color='primary' onClick={(e) => {
                            const settings = this.state.settings
                            settings.theme_settings.items = []
                            settings.theme_settings.addTimeItem('/themes/LightGruvBox.css')
                            this.setState({
                                'settings': settings,
                            })
                        }}>Gruvbox Theme</Button>
                        <br />
                        {this.state.showAdvance ? <>
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Misc Themes</Label>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/HomeBrew.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Homebrew</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Wood.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Wood</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                // Chess.css is shape-only (piece
                                // silhouettes/checkerboard, no colors of
                                // its own by design) - it's meant to be
                                // layered on a color theme rather than
                                // used alone, same pairing pattern as
                                // Apple+Squircle above.
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Wood.css')
                                settings.theme_settings.addTimeItem('/themes/Chess.css')
                                this.setState({ 'settings': settings })
                            }}>Wooden Chess Set</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                // Denya is a chess.com board palette, so it
                                // brings Chess.css's piece shapes along by
                                // default (added second, same pairing
                                // pattern as Wooden Chess Set) rather than
                                // needing a separate combo button.
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Denya.css')
                                settings.theme_settings.addTimeItem('/themes/Chess.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Denya</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                // Self-contained (colors + button/modal
                                // shape ornaments together), same as
                                // Wood.css - no companion shape theme to
                                // add on top.
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/KingdomHearts.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Kingdom Hearts</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                // Self-contained, same as Kingdom Hearts -
                                // no companion shape theme to layer on.
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Halloween.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Halloween</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/CoffeeShop.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Coffee Shop</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Mario.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Mario</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Sunset.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Sunset</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/PeakMusic.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Peak Music</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Apple.css')
                                settings.theme_settings.addTimeItem('/themes/Squircle.css')
                                this.setState({ 'settings': settings })
                            }}>Apple</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Stripe.css')
                                settings.theme_settings.addTimeItem('/themes/Squircle.css')
                                this.setState({ 'settings': settings })
                            }}>Stripe</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Craft.css')
                                settings.theme_settings.addTimeItem('/themes/Squircle.css')
                                this.setState({ 'settings': settings })
                            }}>Craft</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/iOS6.css')
                                this.setState({ 'settings': settings })
                            }}>iOS 6</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/iOS7.css')
                                this.setState({ 'settings': settings })
                            }}>iOS 7</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/LiquidGlass.css')
                                settings.theme_settings.addTimeItem('/themes/Squircle.css')
                                if (!isFirefox()) {
                                    settings.theme_settings.addTimeItem('/themes/MakeItWorkOnChrome.css')
                                }
                                this.setState({ 'settings': settings })
                            }}>Liquid Glass</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Aqua.css')
                                this.setState({ 'settings': settings })
                            }}>Aqua</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Windows7.css')
                                this.setState({ 'settings': settings })
                            }}>Windows 7</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/WindowsXP.css')
                                this.setState({ 'settings': settings })
                            }}>Windows XP</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Windows95.css')
                                this.setState({ 'settings': settings })
                            }}>Windows 95</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/NeXTSTEP.css')
                                this.setState({ 'settings': settings })
                            }}>NeXTSTEP</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Synthwave.css')
                                this.setState({ 'settings': settings })
                            }}>Synthwave</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Dracula.css')
                                this.setState({ 'settings': settings })
                            }}>Dracula</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Nord.css')
                                this.setState({ 'settings': settings })
                            }}>Nord</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/Catppuccin.css')
                                this.setState({ 'settings': settings })
                            }}>Catppuccin</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/TokyoNight.css')
                                this.setState({ 'settings': settings })
                            }}>Tokyo Night</Button>
                            <br />
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Claude Themes</Label>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary'
                                onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/ClaudeDark.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Claude Dark</Button>
                            <Button
                                style={{ 'margin': '8px' }}
                                color='primary'
                                onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.items = []
                                settings.theme_settings.addTimeItem('/themes/ClaudeLight.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Claude Light</Button>
                            <br />
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Additions</Label>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Round.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Rounded Corners</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Squircle.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Squircle Corners</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Chess.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Chess</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/LightElevation1.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Small Elevation</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/LightElevation2.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Medium Elevation</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/LightElevation3.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Strong Elevation</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Elevation.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Extreme Elevation</Button>
                            <br/>
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Workflow Changes</Label>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/QuoteSavePDFEmailRowStaysAtBottom.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Make Bottom Bar Not Always Show</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/FullScreen.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Full Screen</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/StickyTableHeader.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Sticky Table Headers</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/StickyFirstColumn.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Freeze First Column</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/AlwaysShowScrollbars.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Always Show Scrollbars</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/DenseTable.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Compact Table Rows</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/WideMode.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Wide Mode</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/ZebraStripedRows.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Alternating Row Stripes</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/NavLeft.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Left Nav</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/NavRight.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Right Nav</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/NavBottom.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Bottom Nav</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/CompanySkins.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Company Skins</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/MoneyColors.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Money Colors</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/DateColors.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Date Colors</Button>
                            <br/>
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Fonts</Label>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/EasyRead.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Easy-to-Read Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/Elegant.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Elegant Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/CleanSans.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Clean Sans-Serif Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/ClassicSerif.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Classic Serif Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/SFPro.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add San Francisco Style Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/Sakura.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Sakura Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/Rounded.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Rounded Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/SignPainter.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Sign Painter Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/TerminalMono.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Terminal Font</Button>
                            <Button
                                style={{
                                    'margin': '8px'
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Fonts/Hack.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Add Hack Font</Button>
                            <br/>
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Primary Color Dark Themes</Label>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#FC545B',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Red.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Red</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#F5822E',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Orange.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Orange</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#FEC52E',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Yellow.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Yellow</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#4edf4e',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Green2.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Green</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#157EFB',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Blue.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Blue</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#F5539E',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Pink.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Inidigo</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#A453A5',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Dark/Purple.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Violet</Button>
                            <br/>
                            <Label
                                style={{
                                    'fontWeight': '600',
                                    'fontSize': '14pt',
                                }}
                            >Primary Color Light Themes</Label>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#DE3A43',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Red.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Red</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#F5822E',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Orange.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Orange</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#FEC63C',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Yellow.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Yellow</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#65B84D',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Green.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Green</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#157EFB',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Blue.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Blue</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#F5539E',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Pink.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Inidigo</Button>
                            <Button
                                style={{
                                    'margin': '8px',
                                    'backgroundColor': '#944194',
                                }}
                                color='success' onClick={(e) => {
                                const settings = this.state.settings
                                settings.theme_settings.addTimeItem('/themes/Links/Light/Purple.css')
                                this.setState({
                                    'settings': settings,
                                })
                            }}>Violet</Button>
                            </> : <></>
                        }
                    </Col>
                </Row>
                {this.state.showAdvance ? this.renderPreviewHour() : <></>}
                <Row style={{'margin': '8px', 'marginTop': '18px', 'marginBottom': '18px'}}>
                    <Col>
                        <Button color='success' onClick={(e) => {
                            this.updateSettings()
                        }}>Save All Settings</Button>
                    </Col>
                </Row>
            </Container>
        )
    }
}

export default SettingsViewController
